import { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useParams, useNavigate, useLocation } from "react-router-dom";
import { PortfolioColumnRenderer } from './PortfolioComponents';
import { ProjectContentRenderer } from './ProjectPageComponents';
import { projects, getAllTags, getTagBackgroundColor, formatText } from './projectsData';

const NavigationBreadcrumb = ({ projectTitle = null }) => {
    const navigate = useNavigate();
    
    return (
        <div className="w-full px-4">
            <div className="flex items-center text-sm">
                <button 
                    onClick={() => navigate('/')}
                    className="hover:bg-gray-200 px-1 rounded transition-colors duration-200 bg-transparent"
                    style={{color: '#474746'}}
                >
                    Khazanov Anastasia
                </button>
                {projectTitle && (
                    <>
                        <span className="mx-1" style={{color: '#474746'}}>/</span>
                        <span className="px-1" style={{color: '#474746'}}>
                            {projectTitle}
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

function Portfolio() {
    const navigate = useNavigate();
    const [selectedTags, setSelectedTags] = useState([]);

    const allTags = getAllTags();

    const toggleTag = (tag) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter((t) => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const filteredProjects =
        selectedTags.length === 0
            ? projects
            : projects.filter((p) =>
                selectedTags.some((tag) => p.tags.includes(tag))
            );

    return (
        <div className="min-h-screen p-4 lg:p-8" style={{backgroundColor: '#fefffe'}}>
            <NavigationBreadcrumb />

            <header className="text-left mt-10 mb-10 px-4 lg:px-20">
                <h1 className="text-3xl lg:text-4xl font-bold" style={{color: '#37352f'}}>
                    Anastasia Khazanov 
                </h1>
                <div className="mt-4 space-y-0 lg:ml-4">
                    <div className="font-semibold" style={{color: '#474746'}}>
                        Unity Developer focused on gameplay systems, procedural mechanics, tools development and technical art
                    </div>
                    <div className="font-semibold" style={{color: '#474746'}}>
                        Background in contemporary art and interactive media
                    </div>         
                </div>
                <div className="mt-4 space-y-0 lg:ml-4">
                    <div className="font-light mt-8" style={{color: '#474746'}}>
                        Interested in gameplay programming, tools development and technical art roles
                    </div>
                </div>
               
                <div className="mt-4 space-y-4 lg:ml-4">
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                        <div style={{color: '#474746'}}>
                            GitHub: 
                            <a 
                                href="https://github.com/khazanovanastasia" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 ml-1"
                            >
                                github.com/khazanovanastasia
                            </a>
                        </div>
                        <div style={{color: '#474746'}}>
                            LinkedIn: 
                            <a 
                                href="https://www.linkedin.com/in/estherkhazanov" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 ml-1"
                            >
                                www.linkedin.com/in/estherkhazanov
                            </a>
                        </div>
                        <div style={{color: '#474746'}}>
                            Email: 
                            <a 
                                href="mailto:khazanovanastasia@gmail.com" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 ml-1"
                            >
                                khazanovanastasia@gmail.com
                            </a>
                        </div>
                        <div style={{color: '#474746'}}>
                            CV (RU): 
                            <a 
                                href="/cv/CV_Anastasia_Nikolaevskaya.pdf" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 ml-1"
                            >
                                CV_Anastasia_Nikolaevskaya.pdf
                            </a>
                        </div>
                        <div style={{color: '#474746'}}>
                            CV (ENG): 
                            <a 
                                href="/cv/CV_Anastasia_Khazanov.pdf" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 ml-1"
                            >
                                CV_Anastasia_Khazanov.pdf
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            

            {/* Блок с тегами */}
            <div className="flex flex-wrap gap-1 justify-left mb-8 px-4 lg:px-20">
                {allTags.map((tag) => (
                    <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-2 py-0 rounded-md text-gray-800/70 text-sm font-regular transition-all duration-300 transform hover:scale-105 
                            ${selectedTags.includes(tag) ? 'text-gray-800' : 'text-gray-800/70'} 
                            ${getTagBackgroundColor(tag, selectedTags.includes(tag))}`}
                    >
                        {tag}
                    </button>
                ))}
            </div>

            {/* Блоки проектов */}
            <div className="px-4 lg:px-20 space-y-0">
                {filteredProjects.map((project) => (
                    <div
                        key={project.slug}
                        onClick={() => navigate(`${project.slug}`)}
                        className="w-full cursor-pointer hover:bg-gray-50 rounded-lg transition-colors py-4 lg:py-4"
                    >
                        <div className="flex flex-col lg:flex-row w-full gap-0">
                            {project.portfolioLayout.columns.map((column, index) => (
                                <PortfolioColumnRenderer 
                                    key={index} 
                                    column={column} 
                                    project={project}
                                    selectedTags={selectedTags}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    
    return null;
};

function ProjectPage() {
    const { slug } = useParams();
    const project = projects.find((p) => p.slug === slug);

    if (!project) {
        return (
            <div className="min-h-screen p-8" style={{backgroundColor: '#fefffe'}}>
                <NavigationBreadcrumb />
                <div className="p-8">Проект не найден</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 lg:p-8" style={{backgroundColor: '#fefffe'}}>
            <NavigationBreadcrumb projectTitle={project.title} />
            
            <div className="max-w-6xl mx-auto mt-8">
                
                <p className="text-lg mb-6" style={{color: '#474746'}}>
                    {formatText(project.description)}
                </p>
                
                {/* Теги проекта */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {project.tags.map((tag) => (
                        <span
                            key={tag}
                            className={`px-2 py-1 rounded-md text-gray-800/70 text-xs font-regular
                                ${getTagBackgroundColor(tag, false)}`}
                        >
                            {tag}
                        </span>
                    ))}
                </div>
                
                {/* Динамический контент страницы */}
                {project.projectPageContent && project.projectPageContent.map((content, index) => (
                    <ProjectContentRenderer key={index} content={content} />
                ))}
            </div>
        </div>
    );
}

export default function App() {
    return (
        <Router>
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<Portfolio />} />
                <Route path="/:slug" element={<ProjectPage />} />
            </Routes>
        </Router>
    );
}


